import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { NextUIProvider } from "@nextui-org/react";
import "./styles/styles.css";
import Header from "./components/shared/Header";
import ExpiredPage from "./components/pages/ExpiredPage";
import MyURLsPage from "./components/pages/MyURLsPage";
import { NotificationProvider } from "./context/NotificationContext";
import Footer from "./components/shared/Footer";
import PrivacyNotice from "./components/pages/PrivacyNotice";
import AboutShortify from "./components/pages/AboutShortify";
import NotFound from "./components/pages/NotFound";
import LoginPage from "./components/pages/LoginPage";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import EventAdminRoute from "./components/auth/EventAdminRoute";
import LinkTreeView from "./components/pages/LinkTreeView";
import { ConfirmProvider } from "./providers/ConfirmProvider";
import { DisclaimerProvider } from "./context/DisclaimerContext";
import { DisclaimerModal } from "./components/shared/DisclaimerModal";
import { ClientPrincipalProvider } from "./context/ClientPrincipalContext";
import { HomePage } from "./components/pages/home/HomePage";
import Workspace from "./components/shared/Workspace";
import EventsPage from "./components/events/EventsPage";
import CreateEventPage from "./components/events/CreateEventPage";
import RegistrationPage from "./components/registration/RegistrationPage";
import MyRegistrations from "./components/registration/MyRegistrations";
import CheckInPage from "./components/registration/CheckInPage";

const AppContent: React.FC = () => {
  useEffect(() => {
    const handleUnload = () => {
      localStorage.removeItem("shortenedUrls");
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  return (
    <Router>
      <div className="app">
        <DisclaimerModal />
        <Header />
        <div className="main-content">
          <Routes>
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/workspace"
              element={
                <ProtectedRoute>
                  <Workspace />
                </ProtectedRoute>
              }
            />

            <Route
              path="/shortener"
              element={<Navigate to="/workspace?tab=shortener" replace />}
            />
            <Route
              path="/my-link-hub"
              element={<Navigate to="/workspace?tab=linkhub" replace />}
            />
            <Route
              path="/wifiqr"
              element={<Navigate to="/workspace?tab=wify" replace />}
            />

            <Route
              path="/my-urls"
              element={
                <ProtectedRoute>
                  <MyURLsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cgi-events"
              element={
                <ProtectedRoute>
                  <EventAdminRoute>
                    <EventsPage />
                  </EventAdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cgi-events/create"
              element={
                <ProtectedRoute>
                  <EventAdminRoute>
                    <CreateEventPage />
                  </EventAdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cgi-events/:eventId"
              element={
                <ProtectedRoute>
                  <RegistrationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cgi-events/:eventId/register"
              element={
                <ProtectedRoute>
                  <RegistrationPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/my-registrations"
              element={
                <ProtectedRoute>
                  <MyRegistrations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/expired"
              element={
                <ProtectedRoute>
                  <ExpiredPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/privacy"
              element={
                <ProtectedRoute>
                  <PrivacyNotice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/about"
              element={
                <ProtectedRoute>
                  <AboutShortify />
                </ProtectedRoute>
              }
            />
            <Route
              path="/check-in/:checkInCode"
              element={
                <ProtectedRoute>
                  <CheckInPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:cgiCode"
              element={
                <ProtectedRoute>
                  <LinkTreeView />
                </ProtectedRoute>
              }
            />
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
        <Footer />
      </div>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <ClientPrincipalProvider>
      <DisclaimerProvider>
        <NextUIProvider>
          <NotificationProvider>
            <ConfirmProvider>
              <AppContent />
            </ConfirmProvider>
          </NotificationProvider>
        </NextUIProvider>
      </DisclaimerProvider>
    </ClientPrincipalProvider>
  );
};

export default App;
